import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSession } from '@/context/AuthContext'
import { X, Plus, Loader2, ExternalLink, FolderKanban, Lock } from 'lucide-react'
import { useApp } from '@/context/AppContext'
import { TaskItem } from '@/components/TaskItem'
import { effectiveTaskStatus, isPic } from '@/lib/utils'
import type { Task } from '@/lib/types'

/**
 * Drawer geser dari kanan untuk My Dashboard: menampilkan & mengelola task satu
 * project tanpa pindah halaman. Pakai ulang TaskItem (status/edit/subtask/hapus)
 * + quick-add. Izin edit mengikuti aturan halaman project (akses full/assignee,
 * lock prioritas). Server tetap menegakkan otorisasi.
 */
export function ProjectTasksDrawer({ projectId, onClose }: { projectId: string; onClose: () => void }) {
  const { getProject, getProjectTasks, getTaskSubtasks, createTask, getProjectLock } = useApp()
  const { data: session } = useSession()
  const navigate = useNavigate()
  const myUsername = session?.user?.username ?? null

  const project = getProject(projectId)
  const allTasks = getProjectTasks(projectId)
  const lock = getProjectLock(projectId)

  const [access, setAccess] = useState<'full' | 'viewer' | 'assignee' | 'none'>('full')
  const [quickTitle, setQuickTitle] = useState('')
  const [quickSaving, setQuickSaving] = useState(false)
  const [shown, setShown] = useState(false)

  // Mount → animasikan masuk.
  useEffect(() => { setShown(true) }, [])

  // Ambil level akses viewer untuk project ini (sama sumber dgn halaman project).
  useEffect(() => {
    let on = true
    fetch(`/api/projects/${projectId}/access`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (on && d?.access) setAccess(d.access) })
      .catch(() => {})
    return () => { on = false }
  }, [projectId])

  const handleClose = () => { setShown(false); setTimeout(onClose, 180) }

  // Esc untuk menutup.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') handleClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const tasks = useMemo(() => {
    return [...allTasks].sort((a, b) => {
      const ao = a.sortOrder ?? Number.MAX_SAFE_INTEGER
      const bo = b.sortOrder ?? Number.MAX_SAFE_INTEGER
      if (ao !== bo) return ao - bo
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    })
  }, [allTasks])

  const canModify = access === 'full'
  const canEditRow = (t: Task) => access === 'full' || (access === 'assignee' && isPic(t.assignee, myUsername))

  const handleQuickAdd = async () => {
    const t = quickTitle.trim()
    if (!t || quickSaving || lock.locked || !canModify) return
    setQuickSaving(true)
    try {
      await createTask(projectId, t, '', 'medium')
      setQuickTitle('')
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Gagal membuat task')
    } finally {
      setQuickSaving(false)
    }
  }

  if (!project) return null

  const counted = allTasks.filter((t) => !t.recurrence)
  const done = counted.filter((t) => effectiveTaskStatus(t.status, getTaskSubtasks(t.id)) === 'done').length
  const pct = counted.length ? Math.round((done / counted.length) * 100) : 0

  return (
    <div className="fixed inset-0 z-50">
      <div
        className={`absolute inset-0 bg-black/30 backdrop-blur-[2px] transition-opacity duration-200 ${shown ? 'opacity-100' : 'opacity-0'}`}
        onClick={handleClose}
      />
      <div
        className={`absolute right-0 top-0 h-full w-full sm:w-[94vw] sm:max-w-[760px] bg-[#F7F6F2] shadow-2xl shadow-black/20 flex flex-col transition-transform duration-200 ease-out ${shown ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-[#E8E7E3] bg-white flex-shrink-0">
          <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: project.color }} />
          <div className="flex-1 min-w-0">
            <p className="font-display text-[15px] font-bold text-[#1A1916] truncate">{project.name}</p>
            <p className="text-[11px] text-[#8A8A85] font-body">{done}/{counted.length} selesai · {pct}%</p>
          </div>
          <button
            onClick={() => navigate(`/projects/${projectId}`)}
            title="Buka halaman project"
            aria-label="Buka halaman project"
            className="p-2 rounded-lg hover:bg-[#F2F1EC] text-[#AEADA8] hover:text-[#1A1916] transition-colors min-h-[40px] min-w-[40px] flex items-center justify-center"
          >
            <ExternalLink className="w-4 h-4" />
          </button>
          <button
            onClick={handleClose}
            aria-label="Tutup"
            className="p-2 rounded-lg hover:bg-[#F2F1EC] text-[#AEADA8] hover:text-[#1A1916] transition-colors min-h-[40px] min-w-[40px] flex items-center justify-center"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {lock.locked && (
            <div className="flex items-start gap-2 text-[12px] font-body text-[#B45309] bg-[#FEF3C7] border border-[#FDE68A] rounded-xl px-3.5 py-2.5">
              <Lock className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <span>Project terkunci sampai project prioritas di atasnya 100% selesai. Task belum bisa diubah.</span>
            </div>
          )}

          {!lock.locked && canModify && (
            <div className="flex items-center gap-2.5 bg-white border border-[#E8E7E3] rounded-xl px-3.5 py-2.5 focus-within:border-[#C8C7BF] transition-colors">
              <Plus className="w-4 h-4 text-[#C0BDB6] flex-shrink-0" />
              <input
                type="text"
                value={quickTitle}
                onChange={(e) => setQuickTitle(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleQuickAdd() }}
                placeholder="Tambah task cepat — ketik judul lalu Enter"
                className="flex-1 text-sm font-body text-[#1A1916] placeholder:text-[#C0BDB6] bg-transparent border-none outline-none"
              />
              {quickTitle.trim() && (
                <button
                  onClick={handleQuickAdd}
                  disabled={quickSaving}
                  className="flex items-center gap-1.5 px-3 py-1 bg-[#1A1916] text-white rounded-lg text-xs font-medium font-body hover:bg-[#2A2A28] disabled:opacity-50 transition-all flex-shrink-0"
                >
                  {quickSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Tambah'}
                </button>
              )}
            </div>
          )}

          {tasks.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <FolderKanban className="w-8 h-8 text-[#D6D5CF] mb-2" />
              <p className="text-[#8A8A85] font-body text-sm">Belum ada task di project ini.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {tasks.map((t) => (
                <TaskItem key={t.id} task={t} locked={lock.locked} canEdit={canEditRow(t)} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
